import React, { useState, useEffect, forwardRef } from 'react';
import { getBatchEditValue } from '../helpers';
import { Input, TextareaAutosize } from '../components';
import { connectorDriverService } from '../services';
import { useMessage } from '../hooks';

const _AddConnectorDriverForm = (
  { mode, data, submitForm, loading, setLoading, connectorCode, formErrors = {} },
  ref
) => {
  const initialFormState = {
    connector_driver_id: null,
    connector_id: null,
    connector_driver_datasource_type_name: '',
    connector_driver_name: '',
    connector_driver_settings: '',
  };

  const initialBatchEditItems = {};

  const [formData, setFormData] = useState(initialFormState);
  const [defaultSettings, setDefaultSettings] = useState();
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);

  const { showMessage, hideMessage } = useMessage();

  useEffect(() => {
    fetchDefaultSettings();
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        connector_driver_id: data[0].connector_driver_id,
        connector_id: data[0].connector_id,
        connector_driver_datasource_type_name: data[0].connector_driver_datasource_type_name || '',
        connector_driver_name: data[0].connector_driver_name || '',
        connector_driver_settings:
          JSON.stringify(data[0].connector_driver_settings || undefined, null, 2) || '',
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({});
      //} else if(mode === 'add') {
    } else {
      resetForm();
    }
  }, [mode, data]);

  const fetchDefaultSettings = async () => {
    await connectorDriverService
      .getDefaultSettings('odbc')
      .then(res => {
        if (res) {
          data.length === 0 &&
            mode === 'add' &&
            setFormData(prev => {
              return { ...prev, connector_id: res.connector_id };
            });
          setDefaultSettings(res.connector_driver_settings);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = event.target;

    // If input element is a checkbox, we cannot use "value"
    if (event.target.type === 'checkbox') {
      value = event.target.checked;
    }

    switch (connectorCode) {
      case 'odbc':
        if (name.includes('connector_driver_settings_')) {
          let tmpName = name.replace('connector_driver_settings_', '');
          let tmpValue = formData.connector_driver_settings
            ? JSON.parse(formData.connector_driver_settings)
            : {};

          tmpValue.connectionStringMap = {
            ...(tmpValue.connectionStringMap || {}),
            [tmpName]: value,
          };
          name = 'connector_driver_settings';
          value = JSON.stringify(tmpValue, undefined, 2);
        }
        break;
      default:
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData(prev => {
          return { ...prev, [name]: value };
        });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          connector_driver_id: item.connector_driver_id,
          connector_id: item.connector_id,
          connector_driver_datasource_type_name: item.connector_driver_datasource_type_name,
          connector_driver_name: item.connector_driver_name,
          connector_driver_settings: item.connector_driver_settings,
          ...tmp,
        };
      });
    } else {
      dataToSubmit = formData;
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setFormData(prevFormData => {
      return {
        ...initialFormState,
        //connector_driver_settings: JSON.stringify(defaultSettings || undefined, null, 2) || ''
      };
    });
  };

  const renderSettings = () => {
    let settings;

    switch (connectorCode) {
      case 'odbc':
        if (defaultSettings) {
          const settingsObj = formData.connector_driver_settings
            ? JSON.parse(formData.connector_driver_settings).connectionStringMap
            : {};
          settings = (
            <React.Fragment>
              <h4>Connection String Parameter Mapping</h4>
              <div className="form-light-text">
                Map Katalogue parameters to connector-specific parameters.
              </div>
              {Object.entries(defaultSettings.connectionStringMap).map(([key, value]) => {
                const label = key.charAt(0).toUpperCase() + key.slice(1);
                return (
                  <Input
                    key={key}
                    type="text"
                    name={`connector_driver_settings_${key}`}
                    label={label}
                    value={settingsObj[key] || ''}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder={value}
                    labelPosition="left"
                  />
                );
              })}
            </React.Fragment>
          );
        }
        break;

      default:
        settings = (
          <TextareaAutosize
            name="connector_driver_settings"
            label="Settings"
            title=""
            value={formData.connector_driver_settings}
            onChange={handleInputChange}
            disabled={loading}
            className="code"
            placeholder="Settings JSON..."
          />
        );
    }

    return settings;
  };

  const renderConnectionString = () => {
    let str = '';

    if (!(formData && defaultSettings)) {
      return '';
    }

    str += `DRIVER={${formData.connector_driver_name}};`;

    let connectorDriverSettings = formData.connector_driver_settings
      ? JSON.parse(formData.connector_driver_settings).connectionStringMap
      : {};

    Object.entries(defaultSettings.connectionStringMap).forEach(([key, value]) => {
      const param = connectorDriverSettings[key] || value;
      str += `${param}=<${key}>;`;
    });

    return str;
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="connector_driver_name"
              label="Driver Name"
              value={formData.connector_driver_name}
              onChange={handleInputChange}
              disabled={loading}
              title="Name of the ODBC driver. Must exactly match the driver name in the OS ODBC datasource manager to let Katalogue find the driver."
              validation={
                formErrors.connector_driver_name
                  ? { valid: false, message: formErrors.connector_driver_name }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="connector_driver_datasource_type_name"
              label="Datasource Type"
              value={formData.connector_driver_datasource_type_name}
              onChange={handleInputChange}
              disabled={loading}
              title="Name of the connector driver. This is used as the connector display name for all datasources in Katalogue that use this ODBC driver."
              validation={
                formErrors.connector_driver_datasource_type_name
                  ? { valid: false, message: formErrors.connector_driver_datasource_type_name }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && <div className="form-block vertical">{renderSettings()}</div>}
        {!batchEdit && connectorCode === 'odbc' && (
          <div className="form-block vertical disabled">
            <h3>Connection String Preview</h3>
            <div className="form-light-text no-margin">{renderConnectionString()}</div>
          </div>
        )}
      </form>
    </div>
  );
};

export const AddConnectorDriverForm = forwardRef(_AddConnectorDriverForm);
